/**
 * Route-based page view tracking for GA4 + backend analytics
 * Automatically fires page_view events on route changes
 * Uses path deduplication to prevent duplicate React emissions
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { trackPageView, initScrollTracking } from '@/lib/analytics';
import { useBackendAnalytics } from '@/hooks/useBackendAnalytics';
import { useAuth } from '@/hooks/useAuth';

export function usePageTracking() {
  const location = useLocation();
  const { trackPageView: trackBackendPageView } = useBackendAnalytics();
  const { user } = useAuth();
  const lastTrackedPath = useRef<string>('');

  // Track page views on route change (GA4 + backend for authenticated)
  useEffect(() => {
    const path = location.pathname;
    if (path === lastTrackedPath.current) return;
    lastTrackedPath.current = path;

    trackPageView(path, document.title);
    if (user) {
      trackBackendPageView(path, document.title);
    }
  }, [location.pathname, user, trackBackendPageView]);

  // Initialize scroll depth tracking once
  useEffect(() => {
    initScrollTracking();
  }, []);
}
